import { useMemo } from 'react';
import { Globe } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import type { DomainUsageRow } from '@/api/manticore';
import { formatBps, formatBytes } from './_shared/analytics-format';

/** Horizontal share bar — each domain's volume relative to the busiest one. */
function ShareBar({ pct }: { pct: number }) {
  return (
    <div style={{ height: 6, borderRadius: 99, background: 'var(--vn-bg-muted, var(--vn-bg-subtle))', minWidth: 80 }}>
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: 'var(--vn-color-blue-400)' }} />
    </div>
  );
}

/**
 * Top domains by egress volume for the active period — a compact distribution
 * table (domain · volume · peak bandwidth · share bar). Fed by the per-domain
 * usage endpoint, scoped to the same window as the headline metrics.
 */
export function ManticoreTopDomains({
  rows,
  loading,
  periodLabel,
  max = 8,
}: {
  rows: DomainUsageRow[];
  loading?: boolean;
  periodLabel: string;
  max?: number;
}) {
  const top = useMemo(
    () => [...rows].sort((a, b) => b.volume_bytes - a.volume_bytes).slice(0, max),
    [rows, max],
  );
  const peak = top[0]?.volume_bytes ?? 0;

  const columns: ColumnDef<DomainUsageRow, unknown>[] = [
    { id: 'domain', header: 'Domain', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.domain_name}</span> },
    { id: 'volume', header: 'Volume', cell: ({ row }) => formatBytes(row.original.volume_bytes) },
    { id: 'peak', header: 'Peak bandwidth', cell: ({ row }) => formatBps(row.original.peak_bandwidth_bps) },
    {
      id: 'share',
      header: 'Distribution',
      cell: ({ row }) => <ShareBar pct={peak > 0 ? Math.round((row.original.volume_bytes / peak) * 100) : 0} />,
    },
  ];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Top domains</CardTitle>
        <CardDescription>By egress volume ({periodLabel}).</CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton style={{ height: 220 }} />
        ) : (
          <DataTable
            columns={columns}
            data={top}
            getRowId={(r) => r.domain_id}
            emptyState={<EmptyState size="sm" icon={<Globe size={28} />} title="No domains" description="No domain recorded traffic in this period." />}
          />
        )}
      </CardBody>
    </Card>
  );
}
